import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Check,
  ChevronDown,
  CircleAlert,
  CornerDownRight,
  Info,
  Loader2,
  MessageSquareX,
  ShieldAlert,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@nexus/ui";

import type { ScriptReviewBulkDeleteScope } from "@/features/script-review/api/script-review.api";

export interface ScriptReviewModerationCounts {
  aiComments: number;
  /** Standalone human notes (not replies). */
  teamNotes: number;
  /** Human replies threaded under AI comments — they cascade with their comment. */
  replies: number;
}

type DeleteStatus = "idle" | "deleting" | "error";

/** 44px touch targets that relax to the control's compact height on fine pointers. */
const TOUCH_TARGET_SM = "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8";
const TOUCH_TARGET_DEFAULT = "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-9";

interface ImpactRow {
  key: string;
  icon: "trash" | "reply" | "kept";
  label: string;
  outcome: string;
}

/**
 * Adge-admin "Clear comments" control for one review version: a menu with the
 * AI-only and delete-all actions, each behind a confirmation dialog that stays
 * open (with an inline error) when the delete fails.
 */
export function ScriptReviewModerationBar({
  version,
  counts,
  onDelete,
}: {
  version: number;
  counts: ScriptReviewModerationCounts;
  /** Rejects on failure so the dialog can show its error state. */
  onDelete: (scope: ScriptReviewBulkDeleteScope) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<ScriptReviewBulkDeleteScope | null>(null);
  const [status, setStatus] = useState<DeleteStatus>("idle");

  const total = counts.aiComments + counts.teamNotes + counts.replies;
  const isDeleting = status === "deleting";

  const close = () => {
    setScope(null);
    setStatus("idle");
  };

  const confirm = async () => {
    if (!scope) return;
    setStatus("deleting");
    try {
      await onDelete(scope);
      close();
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-sm",
            total > 0 ? "bg-error-subtle text-error-subtle-foreground" : "bg-muted text-muted-foreground",
          )}
        >
          <ShieldAlert className="size-4" aria-hidden />
        </div>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold">
              {t("scriptReview.moderation.title", "Admin moderation")}
            </h2>
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {t("scriptReview.moderation.versionScope", "Version {{n}} only", { n: version })}
            </span>
          </div>
          <p className="text-xs tabular-nums text-muted-foreground">{summaryLine(t, counts)}</p>
        </div>
      </div>

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className={cn("text-error hover:text-error", TOUCH_TARGET_SM)}
            disabled={total === 0}
          >
            <Trash2 className="size-3.5" aria-hidden />
            {t("scriptReview.moderation.trigger", "Clear comments")}
            <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuItem
            variant="destructive"
            className="items-start"
            disabled={counts.aiComments === 0}
            onSelect={() => setScope("ai")}
          >
            <Sparkles className="mt-0.5" aria-hidden />
            <MenuItemText
              title={t("scriptReview.moderation.menu.aiTitle", "Delete AI comments")}
              count={counts.aiComments}
              hint={t(
                "scriptReview.moderation.menu.aiHint",
                "AI findings and their replies. Team notes stay.",
              )}
            />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            className="items-start"
            onSelect={() => setScope("all")}
          >
            <MessageSquareX className="mt-0.5" aria-hidden />
            <MenuItemText
              title={t("scriptReview.moderation.menu.allTitle", "Delete all comments")}
              count={total}
              hint={t(
                "scriptReview.moderation.menu.allHint",
                "Everything on Version {{n}}, including team notes.",
                { n: version },
              )}
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={scope !== null}
        onOpenChange={(next) => {
          if (!next && !isDeleting) close();
        }}
      >
        <DialogContent
          onEscapeKeyDown={(e) => isDeleting && e.preventDefault()}
          onInteractOutside={(e) => isDeleting && e.preventDefault()}
        >
          {scope && (
            <ConfirmBody
              scope={scope}
              version={version}
              counts={counts}
              status={status}
              onCancel={close}
              onConfirm={confirm}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function summaryLine(t: TFunction, counts: ScriptReviewModerationCounts): string {
  const parts = [
    counts.aiComments > 0 &&
      t("scriptReview.moderation.aiCount", { count: counts.aiComments, defaultValue: "{{count}} AI comments" }),
    counts.teamNotes > 0 &&
      t("scriptReview.moderation.noteCount", { count: counts.teamNotes, defaultValue: "{{count}} team notes" }),
    counts.replies > 0 &&
      t("scriptReview.moderation.replyCount", { count: counts.replies, defaultValue: "{{count}} replies" }),
  ].filter((part): part is string => typeof part === "string");
  if (parts.length === 0) {
    return t("scriptReview.moderation.empty", "No comments left on this version.");
  }
  return t("scriptReview.moderation.summary", "{{counts}}. Other versions are not affected.", {
    counts: parts.join(" · "),
  });
}

function MenuItemText({ title, count, hint }: { title: string; count: number; hint: string }) {
  return (
    <span className="flex flex-1 flex-col gap-0.5">
      <span className="flex items-center justify-between gap-2">
        <span className="font-medium">{title}</span>
        <span className="tabular-nums text-muted-foreground">{count}</span>
      </span>
      <span className="text-muted-foreground">{hint}</span>
    </span>
  );
}

function ConfirmBody({
  scope,
  version,
  counts,
  status,
  onCancel,
  onConfirm,
}: {
  scope: ScriptReviewBulkDeleteScope;
  version: number;
  counts: ScriptReviewModerationCounts;
  status: DeleteStatus;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const isAiOnly = scope === "ai";
  const count = isAiOnly ? counts.aiComments : counts.aiComments + counts.teamNotes + counts.replies;
  const isDeleting = status === "deleting";

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isAiOnly
            ? t("scriptReview.moderation.dialog.aiTitle", {
                count,
                n: version,
                defaultValue: "Delete {{count}} AI comments from Version {{n}}?",
              })
            : t("scriptReview.moderation.dialog.allTitle", {
                count,
                n: version,
                defaultValue: "Delete all {{count}} comments from Version {{n}}?",
              })}
        </DialogTitle>
        <DialogDescription>
          {isAiOnly
            ? t(
                "scriptReview.moderation.dialog.aiDescription",
                "Every AI finding on this version's timeline will be removed. This can't be undone.",
              )
            : t(
                "scriptReview.moderation.dialog.allDescription",
                "This clears the timeline for this version, including your team's notes. This can't be undone.",
              )}
        </DialogDescription>
      </DialogHeader>

      <ul className="flex flex-col divide-y divide-border rounded-sm border border-border bg-muted">
        {impactRows(t, scope, counts).map((row) => (
          <li key={row.key} className="flex items-center gap-2.5 px-3 py-2.5 text-sm">
            <ImpactIcon icon={row.icon} />
            <span className="flex-1">{row.label}</span>
            <span
              className={cn(
                "text-xs font-medium",
                row.icon === "kept" ? "text-success-subtle-foreground" : "text-error-subtle-foreground",
              )}
            >
              {row.outcome}
            </span>
          </li>
        ))}
      </ul>

      {isAiOnly && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {t(
            "scriptReview.moderation.dialog.dismissalNote",
            "These findings are recorded as dismissed, so analysis of later versions won't raise them again.",
          )}
        </p>
      )}

      {status === "error" && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-sm border border-error-border bg-error-subtle px-3 py-2.5 text-xs text-error-subtle-foreground"
        >
          <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">
              {t("scriptReview.moderation.dialog.errorTitle", "Couldn't confirm the delete")}
            </span>
            <span>
              {t(
                "scriptReview.moderation.dialog.errorDetail",
                "Check your connection and try again. Retrying won't delete anything twice.",
              )}
            </span>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button
          variant="ghost"
          className={TOUCH_TARGET_DEFAULT}
          onClick={onCancel}
          disabled={isDeleting}
        >
          {t("scriptReview.moderation.dialog.cancel", "Keep comments")}
        </Button>
        <Button
          variant="destructive"
          className={TOUCH_TARGET_DEFAULT}
          onClick={onConfirm}
          disabled={isDeleting}
        >
          {isDeleting && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
          {confirmLabel(t, status, isAiOnly, count)}
        </Button>
      </DialogFooter>
    </>
  );
}

function confirmLabel(t: TFunction, status: DeleteStatus, isAiOnly: boolean, count: number): string {
  if (status === "deleting") {
    return t("scriptReview.moderation.dialog.deleting", {
      count,
      defaultValue: "Deleting {{count}} comments…",
    });
  }
  if (status === "error") {
    return t("scriptReview.moderation.dialog.retry", "Try again");
  }
  return isAiOnly
    ? t("scriptReview.moderation.dialog.confirmAi", {
        count,
        defaultValue: "Delete {{count}} AI comments",
      })
    : t("scriptReview.moderation.dialog.confirmAll", {
        count,
        defaultValue: "Delete {{count}} comments",
      });
}

function impactRows(
  t: TFunction,
  scope: ScriptReviewBulkDeleteScope,
  counts: ScriptReviewModerationCounts,
): ImpactRow[] {
  const deleted = t("scriptReview.moderation.dialog.deleted", "Deleted");
  const rows: ImpactRow[] = [
    {
      key: "ai",
      icon: "trash",
      label: t("scriptReview.moderation.aiCount", {
        count: counts.aiComments,
        defaultValue: "{{count}} AI comments",
      }),
      outcome: deleted,
    },
    {
      key: "replies",
      icon: "reply",
      label: t("scriptReview.moderation.dialog.replyRow", {
        count: counts.replies,
        defaultValue: "{{count}} team replies on those comments",
      }),
      outcome: deleted,
    },
    {
      key: "notes",
      icon: scope === "ai" ? "kept" : "trash",
      label: t("scriptReview.moderation.noteCount", {
        count: counts.teamNotes,
        defaultValue: "{{count}} team notes",
      }),
      outcome:
        scope === "ai" ? t("scriptReview.moderation.dialog.kept", "Kept") : deleted,
    },
  ];
  if (scope === "all") {
    rows.push({
      key: "other-versions",
      icon: "kept",
      label: t("scriptReview.moderation.dialog.otherVersions", "Other versions"),
      outcome: t("scriptReview.moderation.dialog.notAffected", "Not affected"),
    });
  }
  return rows;
}

function ImpactIcon({ icon }: { icon: ImpactRow["icon"] }) {
  if (icon === "kept") {
    return <Check className="size-3.5 shrink-0 text-success" aria-hidden />;
  }
  const Glyph = icon === "reply" ? CornerDownRight : Trash2;
  return <Glyph className="size-3.5 shrink-0 text-error" aria-hidden />;
}
